import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { LoaderService } from '@icc/b2b/sales-document';
import { APP_VERSION, ComplaintSurvey, ComplaintSurveyAnswer, ComplaintSurveyQuestion, ComplaintSurveyResponse, ComplaintSurveySubmission } from '@icc/b2b/shared';
import { TranslateService } from '@icc/shared';
import { ToastService } from '@icc/shared/helpers';
import { ReplaySubject, Subscription } from 'rxjs';

@Component({
    selector: 'icc-complaint-survey',
    templateUrl: './complaint-survey.component.html',
    styleUrls: ['./complaint-survey.component.scss'],
})
export class ComplaintSurveyComponent implements OnInit, OnDestroy {
    static crumbsSubject = new ReplaySubject<Record<string, any>>();

    private subscriptions: Subscription[] = [];

    complaintSurvey: ComplaintSurvey = {};
    complaintSurveyQuestions: ComplaintSurveyQuestion[] = [];
    complaintSurveyAnswers: ComplaintSurveyAnswer[] = [];
    complaintSurveyResponses: ComplaintSurveyResponse[] = [];

    surveySubmissionUuid = '';
    complaintSurveySubmission: ComplaintSurveySubmission = {} as ComplaintSurveySubmission;
    surveyLoadedAt: Date = new Date();

    surveyForm: FormGroup;
    submitted = false;
    submissionSuccess = false;
    submissionError = false;

    submissionOnceCompleted = false;
    previewMode = false;

    constructor(
        private loaderService: LoaderService,
        private http: HttpClient,
        @Inject(APP_VERSION) private appVersion: string,
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private toastService: ToastService,
        private translateService: TranslateService,
    ) {
        this.surveyForm = this.fb.group({
            responses: this.fb.array([]),
        });
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.route.data.subscribe(data => {
                this.previewMode = data['previewMode'] || false;
            }),
            this.route.params.subscribe(params => {
                if (params['surveySubmissionUuid']) {
                    this.surveySubmissionUuid = params['surveySubmissionUuid'];
                    this.getComplaintSurvey();
                }
            }),
            this.surveyForm.valueChanges.subscribe(() => {
                if ((this.submissionError || this.submitted) && this.hasAtLeastOneAnswer()) {
                    this.submissionError = false;
                    this.submitted = false;
                }
            })
        );
    }

    setBreadcrumbs(): void {
        if (this.previewMode && this.complaintSurveySubmission?.documentId) {
            const surveyLabel = this.translateService.instant('COMPLAINT|Podgląd ankiety');
            ComplaintSurveyComponent.crumbsSubject.next({
                level: 3,
                crumbs: [
                    {
                        label: surveyLabel,
                        url: `/trade/complaint-survey/${this.surveySubmissionUuid}/preview`,
                        documentId: this.complaintSurveySubmission.documentId,
                    },
                ],
            });
        }
    }

    async getComplaintSurvey() {
        this.loaderService.setLoadingAfterTime();
        this.surveyLoadedAt = new Date();

        const serverComplaintSurvey = await this.http.post<{
            complaintSurvey: ComplaintSurvey,
            complaintSurveyQuestions: ComplaintSurveyQuestion[],
            complaintSurveyAnswers: ComplaintSurveyAnswer[],
            complaintSurveySubmission: ComplaintSurveySubmission,
            complaintSurveyResponses: ComplaintSurveyResponse[]
        }>(`${window.location.origin}/api/b2b/complaint-survey`, {
            surveySubmissionUuid: this.surveySubmissionUuid,
            appVersion: this.appVersion
        }).toPromise();

        if (serverComplaintSurvey) {
            this.complaintSurvey = serverComplaintSurvey.complaintSurvey;
            this.complaintSurveyQuestions = serverComplaintSurvey.complaintSurveyQuestions;
            this.complaintSurveyAnswers = serverComplaintSurvey.complaintSurveyAnswers;
            this.complaintSurveySubmission = serverComplaintSurvey.complaintSurveySubmission;
            this.complaintSurveyResponses = serverComplaintSurvey.complaintSurveyResponses || [];

            if (this.complaintSurveySubmission) {
                this.submissionOnceCompleted = this.complaintSurveySubmission.isCompleted || false;
            }

            this.initializeFormControls();
            if (this.previewMode) {
                this.disableFormControls();
            }
            this.setBreadcrumbs();
        }
        this.loaderService.setLoaded();
    }

    initializeFormControls() {
        const responsesArray = this.surveyForm.get('responses') as FormArray;

        while (responsesArray.length) {
            responsesArray.removeAt(0);
        }

        this.complaintSurveyQuestions.forEach(question => {
            if (question.type === 'text') {
                const existingResponse = this.complaintSurveyResponses.find(r => r.complaintSurveyQuestionId === question.id);
                responsesArray.push(this.fb.group({
                    questionId: [question.id],
                    type: [question.type],
                    textResponse: [existingResponse?.textResponse || '']
                }));
            } else if (question.type === 'single_choice') {
                const existingResponse = this.complaintSurveyResponses.find(r => r.complaintSurveyQuestionId === question.id);
                responsesArray.push(this.fb.group({
                    questionId: [question.id],
                    type: [question.type],
                    answerId: [existingResponse?.complaintSurveyAnswerId || null]
                }));
            } else if (question.type === 'multiple_choice') {
                const answersControls: { [key: string]: [boolean] } = {};
                const questionAnswers = this.getAnswersForQuestion(question.id || 0);

                questionAnswers.forEach(answer => {
                    const existingResponse = this.complaintSurveyResponses.find(r => 
                        r.complaintSurveyQuestionId === question.id && 
                        r.complaintSurveyAnswerId === answer.id
                    );
                    answersControls[`answer_${answer.id}`] = [existingResponse ? !!existingResponse.multipleChoiceSelected : false];
                });

                responsesArray.push(this.fb.group({
                    questionId: [question.id],
                    type: [question.type],
                    answers: this.fb.group(answersControls)
                }));
            }
        });
    }

    getAnswersForQuestion(questionId: number) {
        return this.complaintSurveyAnswers.filter((answer) => answer.complaintSurveyQuestionId === questionId);
    }

    getResponseControls() {
        return (this.surveyForm.get('responses') as FormArray).controls;
    }

    getMultipleChoiceControls(index: number) {
        const responseGroup = this.getResponseControls()[index] as FormGroup;
        return (responseGroup.get('answers') as FormGroup).controls;
    }

    onRadioValueChange(questionIndex: number, value: any) {
        const responsesArray = this.surveyForm.get('responses') as FormArray;
        const questionGroup = responsesArray.at(questionIndex) as FormGroup;
        questionGroup.get('answerId')?.setValue(value);
    }

    hasAtLeastOneAnswer(): boolean {
        const responseControls = this.getResponseControls();

        for (const control of responseControls) {
            const formValue = control.value;
            const questionType = formValue.type;

            if (questionType === 'text' && formValue.textResponse?.trim()) {
                return true;
            } else if (questionType === 'single_choice' && formValue.answerId) {
                return true;
            } else if (questionType === 'multiple_choice') {
                const answers = formValue.answers;
                for (const key in answers) {
                    if (answers[key]) {
                    return true;
                    }
                }
            }
        }

        return false;
    }

    async submitSurvey() {
        this.submitted = true;

        if (!this.hasAtLeastOneAnswer()) {
            this.submissionError = true;
            this.toastService.add({
                severity: 'error',
                title: this.translateService.instant('COMPLAINT|Nie można wysłać ankiety.'),
                body: this.translateService.instant('COMPLAINT|W celu przesłania ankiety należy odpowiedzieć na przynajmniej jedno pytanie.'),
            });
            return;
        }

        this.loaderService.setLoadingAfterTime();

        try {
            const submissionUuid = this.complaintSurveySubmission.uuid;

            const responseData: ComplaintSurveyResponse[] = [];
            const responseControls = this.getResponseControls();

            responseControls.forEach(control => {
                const formValue = control.value;
                const questionId = formValue.questionId;
                const questionType = formValue.type;

                if (questionType === 'text' && formValue.textResponse?.trim()) {
                    responseData.push({
                        complaintSurveySubmissionId: submissionUuid,
                        complaintSurveyQuestionId: questionId,
                        complaintSurveyAnswerId: null,
                        textResponse: formValue.textResponse,
                        multipleChoiceSelected: null
                    });
                } else if (questionType === 'single_choice' && formValue.answerId) {
                    responseData.push({
                        complaintSurveySubmissionId: submissionUuid,
                        complaintSurveyQuestionId: questionId,
                        complaintSurveyAnswerId: formValue.answerId,
                        textResponse: null,
                        multipleChoiceSelected: null
                    });
                } else if (questionType === 'multiple_choice') {
                    const answers = formValue.answers;

                    Object.keys(answers).forEach(key => {
                        if (answers[key]) {
                            const answerId = parseInt(key.replace('answer_', ''));
                            responseData.push({
                                complaintSurveySubmissionId: submissionUuid,
                                complaintSurveyQuestionId: questionId,
                                complaintSurveyAnswerId: answerId,
                                textResponse: null,
                                multipleChoiceSelected: true
                            });
                        }
                    });
                }
            });

            if (responseData.length > 0) {
                const result = await this.http.post<{success: boolean, error?: string}>(`${window.location.origin}/api/b2b/complaint-survey-submit`, {
                    submission: this.complaintSurveySubmission,
                    responses: responseData,
                    surveyLoadedAt: this.surveyLoadedAt.toISOString(),
                    appVersion: this.appVersion
                }).toPromise();

                if (result && result.success) {
                    this.submissionSuccess = true;
                } else {
                    this.submissionError = true;
                }
            } else {
                this.submissionError = true;
            }
        } catch (error) {
            this.submissionError = true;
            if (error.error.message === 'survey-modified') {
                this.toastService.add({
                title: this.translateService.instant('COMPLAINT|Nowa wersja ankiety'),
                body: this.translateService.instant('COMPLAINT|Dostępna jest nowa wersja ankiety. Odśwież stronę, aby zobaczyć aktualną wersję.'),
                severity: 'error'
            });
            } else {
                this.toastService.add({
                    title: this.translateService.instant('INTERFACE|Błąd'),
                    body: this.translateService.instant('COMPLAINT|Wystąpił błąd podczas wysyłania ankiety. Spróbuj ponownie.'),
                    severity: 'error'
                });
            }
        } finally {
            this.loaderService.setLoaded();
        }
    }

    private disableFormControls() {
        if (!this.previewMode) return;

        const responsesArray = this.surveyForm.get('responses') as FormArray;
        responsesArray.controls.forEach(control => {
            const responseGroup = control as FormGroup;

            if (responseGroup.get('textResponse')) {
                responseGroup.get('textResponse')?.disable();
            }

            if (responseGroup.get('answerId')) {
                responseGroup.get('answerId')?.disable();
            }

            if (responseGroup.get('answers')) {
                const answersGroup = responseGroup.get('answers') as FormGroup;
                Object.keys(answersGroup.controls).forEach(key => {
                    answersGroup.get(key)?.disable();
                });
            }
        });
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
